<script lang="ts" setup>
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import Listbox from 'primevue/listbox'
import Select from 'primevue/select'
import ProgressSpinner from 'primevue/progressspinner'
import { computeChange, computeSurcharge, rowDue } from '../../utils/tender.calculations'
import type { PaymentMethod } from '~/stores/admin/paymentMethods'
import type { Discount } from '~/stores/admin/discounts'
import type { CheckTender } from '~/types/checks'

const { t } = useNuxtApp().$i18n

const posCheckStore = usePosCheckStore()
const paymentMethodsStore = usePaymentMethodsStore()
const discountsStore = useDiscountsStore()
const businessStore = useBusinessStore()

const { activePaymentMethods: paymentMethods } = storeToRefs(paymentMethodsStore)
const { discounts } = storeToRefs(discountsStore)
const { serviceType } = storeToRefs(businessStore)
const {
  subtotal,
  selectedPaymentMethod,
  selectedDiscount,
  discountAmount,
  subtotalAfterDiscount,
  paymentSurcharge,
  finalTotal,
  canConfirmPayment,
} = storeToRefs(posCheckStore)

const props = defineProps<{
  visible: boolean
  submitting?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', data: {
    tax: number
    discount_amount: number
    tip: number
    payment_method_id: number | null
    payment_method_name: string | null
    discount_id: number | null
    discount_percentage: number | null
    tenders: CheckTender[]
  }): void
}>()

// Single-payment local state
const tip = ref<number | null>(null)
const selectedTipOption = ref<string | null>(null)
const selectedPaymentMethodLocal = ref<PaymentMethod | null>(null)
const selectedDiscountLocal = ref<Discount | null>(null)
const cashReceived = ref(0)

// Split mode state
interface TenderRow {
  method: PaymentMethod | null
  amount_applied: number
  amount_tendered: number
  change_given: number
  surcharge: number
}
const isSplitMode = ref(false)
const tenderRows = ref<TenderRow[]>([])

// Cash detection via is_cash flag — replaces fragile name-matching
const isCashPayment = computed(() => selectedPaymentMethodLocal.value?.is_cash === true)

const changeAmount = computed(() => {
  const change = cashReceived.value - finalTotal.value
  return change >= 0 ? change : 0
})

// Split mode computed — tenders must cover subtotal after discount + tip
const splitTarget = computed(() => subtotalAfterDiscount.value + (tip.value || 0))

const splitRemaining = computed(() => {
  const applied = tenderRows.value.reduce((sum, r) => sum + (r.amount_applied || 0), 0)
  return Number((splitTarget.value - applied).toFixed(2))
})

const canSubmitSplit = computed(() =>
  splitRemaining.value === 0
  && tenderRows.value.every(r =>
    r.method !== null
    && r.amount_applied > 0
    && (r.method.is_cash ? r.amount_tendered >= rowDue(r) : true),
  ),
)

const canSubmit = computed(() => {
  if (isSplitMode.value) return canSubmitSplit.value
  if (!canConfirmPayment.value) return false
  if (isCashPayment.value) return cashReceived.value >= finalTotal.value
  return true
})

// Auto-select the cash method whenever payment methods become available.
// watchEffect re-runs if paymentMethods loads after mount (async fetch race).
watchEffect(() => {
  if (selectedPaymentMethodLocal.value === null && paymentMethods.value.length > 0) {
    const cashMethod = paymentMethods.value.find(pm => pm.is_cash)
    if (cashMethod) selectedPaymentMethodLocal.value = cashMethod
  }
})

// immediate so the store mirror is synced even when watchEffect auto-selects the
// cash method during setup (payment methods already loaded) — otherwise the lazy
// watch misses that initial assignment and canConfirmPayment stays false.
watch(selectedPaymentMethodLocal, (method, oldMethod) => {
  posCheckStore.setSelectedPaymentMethod(method?.id || null)
  if (oldMethod?.is_cash && !method?.is_cash) cashReceived.value = 0
}, { immediate: true })

watch(selectedDiscountLocal, discount => posCheckStore.setSelectedDiscount(discount?.id || null))
watch(tip, amount => posCheckStore.setTipAmount(amount || 0))

watch(cashReceived, (newAmount, oldAmount) => {
  if (oldAmount === 0 && newAmount > 0 && !isCashPayment.value) {
    const cashMethod = paymentMethods.value.find(pm => pm.is_cash)
    if (cashMethod) selectedPaymentMethodLocal.value = cashMethod
  }
})

// ─── Split mode ───────────────────────────────────────────────────────────────

function enterSplitMode() {
  isSplitMode.value = true
  const amount = splitTarget.value
  const surcharge = computeSurcharge(selectedPaymentMethodLocal.value, amount)
  tenderRows.value = [{
    method: selectedPaymentMethodLocal.value,
    amount_applied: amount,
    amount_tendered: amount + surcharge,
    change_given: 0,
    surcharge,
  }]
}

function exitSplitMode() {
  isSplitMode.value = false
  tenderRows.value = []
}

function addTenderRow() {
  const remaining = Math.max(0, splitRemaining.value)
  tenderRows.value.push({
    method: null,
    amount_applied: remaining,
    amount_tendered: remaining,
    change_given: 0,
    surcharge: 0,
  })
}

function removeRow(index: number) {
  tenderRows.value.splice(index, 1)
  if (tenderRows.value.length <= 1) exitSplitMode()
}

function onRowMethodChange(index: number) {
  const row = tenderRows.value[index]
  if (!row.method) return
  row.surcharge = computeSurcharge(row.method, row.amount_applied)
  row.amount_tendered = rowDue(row)
  row.change_given = 0
}

function onRowAmountAppliedChange(index: number) {
  const row = tenderRows.value[index]
  row.surcharge = computeSurcharge(row.method, row.amount_applied || 0)
  if (row.method && !row.method.is_cash) {
    row.amount_tendered = rowDue(row)
    row.change_given = 0
  }
  else {
    row.change_given = computeChange(row, row.amount_tendered)
  }
}

function onRowAmountTenderedChange(index: number) {
  const row = tenderRows.value[index]
  if (!row.method?.is_cash) return
  row.change_given = computeChange(row, row.amount_tendered)
}

// ─── Tip ──────────────────────────────────────────────────────────────────────

function handleTipPercentage(percentage: number) {
  selectedTipOption.value = `${percentage}%`
  tip.value = Math.round((subtotalAfterDiscount.value * percentage) / 100)
}

function handleTipOther() {
  selectedTipOption.value = 'other'
  tip.value = null
}

// ─── Dialog lifecycle ─────────────────────────────────────────────────────────

function handleClose() {
  if (props.submitting) return
  emit('update:visible', false)
}

// The dialog is v-if-mounted by CheckCart, so unmount IS the reset. Resetting on
// confirm instead would wipe the form when the close request fails.
onUnmounted(() => posCheckStore.resetPaymentState())

// ─── Confirm ──────────────────────────────────────────────────────────────────

function handleConfirm() {
  if (props.submitting) return
  if (isSplitMode.value) {
    if (!canSubmitSplit.value) return
    const tenders: CheckTender[] = tenderRows.value.map(row => ({
      method_id: row.method!.id,
      method_name: row.method!.name,
      is_cash: row.method!.is_cash,
      amount_tendered: row.amount_tendered,
      amount_applied: row.amount_applied,
      change_given: row.change_given,
      surcharge: row.surcharge,
    }))
    emit('confirm', {
      tax: 0,
      discount_amount: discountAmount.value,
      tip: tip.value || 0,
      payment_method_id: tenders[0].method_id ?? null,
      payment_method_name: tenders[0].method_name ?? null,
      discount_id: selectedDiscount.value?.id || null,
      discount_percentage: selectedDiscount.value?.percentage || null,
      tenders,
    })
  }
  else {
    if (!canConfirmPayment.value) return
    const method = selectedPaymentMethod.value
    const applied = subtotalAfterDiscount.value + (tip.value || 0)
    const tender: CheckTender = {
      method_id: method?.id ?? null,
      method_name: method?.name ?? 'Unknown',
      is_cash: selectedPaymentMethodLocal.value?.is_cash ?? false,
      amount_tendered: isCashPayment.value ? cashReceived.value : finalTotal.value,
      amount_applied: applied,
      change_given: isCashPayment.value ? Math.max(0, cashReceived.value - finalTotal.value) : 0,
      surcharge: paymentSurcharge.value,
    }
    emit('confirm', {
      tax: 0,
      discount_amount: discountAmount.value,
      tip: tip.value || 0,
      payment_method_id: method?.id || null,
      payment_method_name: method?.name || null,
      discount_id: selectedDiscount.value?.id || null,
      discount_percentage: selectedDiscount.value?.percentage || null,
      tenders: [tender],
    })
  }
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :header="t('Close Check')"
    position="center"
    :closable="!submitting"
    class="w-full max-w-5xl"
    @update:visible="handleClose"
  >
    <div class="relative">
      <div
        v-if="submitting"
        class="absolute inset-0 z-10 flex items-center justify-center bg-white/70"
      >
        <ProgressSpinner style="width: 3rem; height: 3rem" />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Left Column: Payment / Split rows -->
        <div
          v-if="!isSplitMode"
          class="flex flex-col gap-4"
        >
          <!-- Payment Method Selection -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-700">
              {{ t('Payment Method') }} <span class="text-red-500">*</span>
            </label>
            <Listbox
              v-model="selectedPaymentMethodLocal"
              :options="paymentMethods"
              option-label="name"
              :placeholder="t('Select payment method')"
              class="w-full"
              :pt="{
                option: {
                  class: 'bg-gray-200 border-2 border-solid border-gray-400 rounded',
                },
              }"
            />
            <span
              v-if="selectedPaymentMethodLocal && selectedPaymentMethodLocal.surcharge_percentage > 0"
              class="text-xs text-orange-600"
            >
              {{ t('Surcharge') }}: {{ selectedPaymentMethodLocal.surcharge_percentage }}%
            </span>
          </div>

          <!-- Discount Selection -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-700">{{ t('Discount') }}</label>
            <Listbox
              v-model="selectedDiscountLocal"
              :options="discounts"
              :placeholder="t('Select discount')"
              class="w-full"
              :pt="{
                option: {
                  class: 'bg-gray-200 border-2 border-solid border-gray-400 rounded',
                },
              }"
            >
              <template #option="slotProps">
                <div class="flex items-center">
                  <div>{{ slotProps.option.percentage }}%</div>
                </div>
              </template>
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  {{ slotProps.value.percentage }}%
                </div>
                <span v-else>{{ t('Select discount') }}</span>
              </template>
            </Listbox>
          </div>

          <!-- Tip Input -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-gray-700">{{ t('Tip') }}</label>
            <div class="grid grid-cols-5 gap-2">
              <Button
                v-for="percentage in [5, 10, 15, 20]"
                :key="percentage"
                :label="`${percentage}%`"
                :severity="selectedTipOption === `${percentage}%` ? 'primary' : 'secondary'"
                size="large"
                class="h-14 text-lg font-semibold"
                @click="handleTipPercentage(percentage)"
              />
              <Button
                :label="t('Other')"
                :severity="selectedTipOption === 'other' ? 'primary' : 'secondary'"
                size="large"
                class="h-14 text-lg font-semibold"
                @click="handleTipOther"
              />
            </div>
            <InputNumber
              v-if="selectedTipOption === 'other'"
              id="tip"
              v-model="tip"
              mode="currency"
              currency="USD"
              locale="en-US"
              :placeholder="t('Enter tip amount')"
              :min="0"
              :min-fraction-digits="0"
              class="w-full"
            />
          </div>

          <!-- Split payment button — restaurant only -->
          <Button
            v-if="serviceType === 'restaurant'"
            :label="t('Split payment')"
            icon="pi pi-share-alt"
            severity="secondary"
            outlined
            class="w-full mt-auto"
            @click="enterSplitMode"
          />
        </div>

        <!-- Split mode: spans left + middle columns -->
        <div
          v-else
          class="md:col-span-2 flex flex-col gap-3"
        >
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-700">
              {{ t('Split payment') }}
            </h3>
            <Button
              :label="t('Use single payment')"
              severity="secondary"
              size="small"
              text
              @click="exitSplitMode"
            />
          </div>

          <!-- Tender rows -->
          <div
            v-for="(row, i) in tenderRows"
            :key="i"
            class="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div class="flex gap-2 items-end">
              <!-- Method -->
              <div class="flex-1">
                <label class="text-xs text-gray-500 block mb-1">{{ t('Payment Method') }}</label>
                <Select
                  v-model="row.method"
                  :options="paymentMethods"
                  option-label="name"
                  :placeholder="t('Select payment method')"
                  class="w-full"
                  @update:model-value="onRowMethodChange(i)"
                />
              </div>

              <!-- Applied -->
              <div class="w-36">
                <label class="text-xs text-gray-500 block mb-1">{{ t('Applied') }}</label>
                <InputNumber
                  v-model="row.amount_applied"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  :min="0.01"
                  :min-fraction-digits="2"
                  class="w-full"
                  @update:model-value="onRowAmountAppliedChange(i)"
                />
              </div>

              <!-- Remove -->
              <Button
                v-if="tenderRows.length > 1"
                icon="pi pi-times"
                severity="danger"
                text
                rounded
                @click="removeRow(i)"
              />
            </div>

            <!-- Cash-only: tendered + change -->
            <div
              v-if="row.method?.is_cash"
              class="flex gap-2 items-end"
            >
              <div class="w-36">
                <label class="text-xs text-gray-500 block mb-1">{{ t('Tendered') }}</label>
                <InputNumber
                  v-model="row.amount_tendered"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  :min="rowDue(row)"
                  :min-fraction-digits="2"
                  class="w-full"
                  @update:model-value="onRowAmountTenderedChange(i)"
                />
              </div>
              <div
                v-if="row.change_given > 0"
                class="text-sm text-green-700 pb-2"
              >
                <span class="text-xs text-gray-500 block">{{ t('Change') }}</span>
                {{ usePriceFormatter(row.change_given) }}
              </div>
            </div>

            <!-- Surcharge badge -->
            <span
              v-if="row.surcharge > 0"
              class="text-xs text-orange-600"
            >
              {{ t('Surcharge') }}: {{ usePriceFormatter(row.surcharge) }}
            </span>
          </div>

          <!-- Remaining balance -->
          <div class="flex justify-between text-base font-semibold border-t pt-3">
            <span :class="splitRemaining === 0 ? 'text-green-600' : splitRemaining < 0 ? 'text-red-600' : 'text-gray-700'">
              {{ splitRemaining < 0 ? t('Overpaid') : t('Remaining') }}:
            </span>
            <span :class="splitRemaining === 0 ? 'text-green-600' : 'text-red-600'">
              {{ usePriceFormatter(Math.abs(splitRemaining)) }}
            </span>
          </div>

          <!-- Add payment method -->
          <Button
            :label="t('Add payment method')"
            icon="pi pi-plus"
            severity="secondary"
            outlined
            class="w-full"
            :disabled="splitRemaining <= 0"
            @click="addTenderRow"
          />
        </div>

        <!-- Middle Column: Cash NumberPad (single mode only) -->
        <div
          v-if="!isSplitMode"
          class="flex flex-col gap-4"
        >
          <div>
            <h3 class="text-lg font-semibold mb-4">
              {{ t('Cash Received') }}
            </h3>
            <NumberPad v-model="cashReceived" />
          </div>
        </div>

        <!-- Right Column: Summary -->
        <div class="flex flex-col gap-3 bg-gray-50 p-4 rounded-lg">
          <h3 class="text-lg font-semibold text-gray-800 mb-2">
            {{ t('Summary') }}
          </h3>

          <div class="flex justify-between text-base">
            <span class="text-gray-600">{{ t('Subtotal') }}:</span>
            <span class="font-medium">{{ usePriceFormatter(subtotal) }}</span>
          </div>

          <div
            v-if="discountAmount > 0"
            class="flex justify-between text-base text-green-600"
          >
            <span>{{ t('Discount') }} ({{ selectedDiscountLocal?.percentage }}%):</span>
            <span class="font-medium">-{{ usePriceFormatter(discountAmount) }}</span>
          </div>

          <div
            v-if="discountAmount > 0"
            class="flex justify-between text-base border-t pt-2"
          >
            <span class="text-gray-600">{{ t('Subtotal') }}:</span>
            <span class="font-medium">{{ usePriceFormatter(subtotalAfterDiscount) }}</span>
          </div>

          <div
            v-if="tip && tip > 0"
            class="flex justify-between text-base"
          >
            <span class="text-gray-600">{{ t('Tip') }}:</span>
            <span class="font-medium">{{ usePriceFormatter(tip) }}</span>
          </div>

          <div
            v-if="paymentSurcharge > 0"
            class="flex justify-between text-base text-orange-600"
          >
            <span>{{ t('Surcharge') }} ({{ selectedPaymentMethodLocal?.surcharge_percentage }}%):</span>
            <span class="font-medium">+{{ usePriceFormatter(paymentSurcharge) }}</span>
          </div>

          <div class="flex justify-between text-xl font-bold border-t-2 pt-3 mt-2">
            <span class="text-gray-800">{{ t('Final Total') }}:</span>
            <span class="text-green-600">{{ usePriceFormatter(finalTotal) }}</span>
          </div>

          <!-- Single mode: change display -->
          <div
            v-if="!isSplitMode && cashReceived > 0 && changeAmount >= 0"
            class="flex justify-between text-base mt-2 pt-2 border-t"
          >
            <span class="text-gray-600">{{ t('Change') }}:</span>
            <span class="font-medium text-green-600">{{ usePriceFormatter(changeAmount) }}</span>
          </div>

          <div
            v-if="!isSplitMode && cashReceived > 0 && cashReceived < finalTotal"
            class="mt-2 text-sm text-red-600 text-center"
          >
            {{ t('Insufficient cash amount') }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex gap-2 justify-end">
        <Button
          :label="t('Cancel')"
          severity="secondary"
          outlined
          :disabled="submitting"
          @click="handleClose"
        />
        <Button
          :label="t('Close & Pay')"
          severity="success"
          icon="pi pi-check"
          :loading="submitting"
          :disabled="!canSubmit || submitting"
          @click="handleConfirm"
        />
      </div>
    </template>
  </Dialog>
</template>
